import { Injectable } from '@angular/core';
import { DayOfWeek, FeatureFlags } from '../enums/generated.enums';
import { ApiConversionHelper } from '../helpers/api-conversion.helper';
import { CurrencyHelper } from '../helpers/currency.helper';
import { DateHelper } from '../helpers/date.helper';
import { TextHelper } from '../helpers/text.helper';
import { OrganizationName } from '../types/organization-name.type';
import { OrganizationConfigurationUi } from '../types/organization.type';
import { AuthService } from './auth.service';
import { DataAvailabilityService } from './data-availability.service';
import { LoggingService } from './logging.service';
import { NavigationService } from './navigation.service';
import { SidepanelService } from './sidepanel.service';
import { SignalRService } from './signalr.service';
import { StorageService } from './storage.service';
import { EventBusService, EventType } from './event-bus.service';
import { ApiService } from './api/api.service';
import { TenantsService } from './tenants.service';

@Injectable({ providedIn: 'root' })
export class OrganizationService { // organizations
  private _organizations: OrganizationName[];
  private _organizationConfigurationUi: OrganizationConfigurationUi;

  get selectedOrganization(): OrganizationName {
    if (this._organizations == null || this.authService.currentOrganization == null) {
      return null;
    }
    return this._organizations.find(o => o.name === this.authService.currentOrganization) ?? null;
  }

  get isDemoOrganization(): boolean {
    return this.selectedOrganization?.name === 'UiPathDemo' || this.selectedOrganization?.name === 'BluePrismDemo';
  }

  get useDemoIncidents(): boolean {
    return this.authService.hasFeatureFlag(FeatureFlags.UseDemoIncidents);
  }

  get isTestOrganization(): boolean {
    return this.selectedOrganization?.name === 'TestOrganization';
  }

  get organizations(): OrganizationName[] {
    return this._organizations;
  }

  get organizationConfigurationUi(): OrganizationConfigurationUi {
    return this._organizationConfigurationUi;
  }

  get isReadOnly(): boolean {
    return this.organizationConfigurationUi?.synchronizeFromOnPrem ?? false;
  }

  get is24HFormat(): boolean {
    return this.organizationConfigurationUi?.is24HFormat;
  }

  get firstDayOfWeek(): DayOfWeek {
    return this.organizationConfigurationUi ? this.organizationConfigurationUi.firstDayOfWeek : DayOfWeek.Monday;
  }

  get isDemoOrTestOrganization(): boolean {
    return (this.selectedOrganization && this.isDemoOrganization) || this.isTestOrganization;
  }

  get lastDate(): Date {
    return DateHelper.startOfToday();
  }

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private loggingService: LoggingService,
    private signalRService: SignalRService,
    private storageService: StorageService,
    private dataAvailabilityService: DataAvailabilityService,
    private sidepanelService: SidepanelService,
    private navigationService: NavigationService,
    private eventBusService: EventBusService,
    private tenantsService: TenantsService,
  ) {}

  clear(): void {
    // clear for organizations selection
    this._organizations = null;
    this._organizationConfigurationUi = null;
  }

  async initializeOrganizationsIfNeeded(): Promise<void> {
    if (this.organizations == null) {
      await this.initializeOrganizations();
    }
    // TODO: find a better way to set the currency
    // analyses could have different currencies (uses process.currency) so its need to reset the currency after each analysis
    if (this.organizationConfigurationUi != null) {
      CurrencyHelper.setCurrency(this.organizationConfigurationUi.currencyCode);
    }
  }

  private async initializeOrganizations(): Promise<void> {
    let organizations: OrganizationName[] = [];
    try {
      organizations = await this.apiService.auth.getOrganizationNames();
      organizations = organizations.sort((a, b) => a.label.localeCompare(b.label));
    } catch (error: any) {
      this.loggingService.logException(error);
      return;
    }
    organizations.forEach(o => {
      o.urlName = TextHelper.getUrlName(o.name);
      o.lowerCaseName = o.name.toLowerCase();
    });
    this._organizations = organizations;
    await this.initializeOrganizationConfigurationUi();
    await this.tenantsService.initializeTenants();
  }

  public async initializeOrganizationConfigurationUi(): Promise<void> {
    try {
      this._organizationConfigurationUi = await this.apiService.auth.getOrganizationConfiguration();
    } catch (error: any) {
      this.loggingService.logException(error);
      this._organizationConfigurationUi = null;
    }

    try {
      DateHelper.setTimeZone(this.organizationConfigurationUi.ianaTimeZoneId);
      DateHelper.setDay24HFormat(this.organizationConfigurationUi.is24HFormat);
    } catch (error: any) {
      this.loggingService.logException(error);
    }

    CurrencyHelper.setCurrency(this.organizationConfigurationUi.currencyCode);
    ApiConversionHelper.isBusinessExceptionSuccess = this.organizationConfigurationUi.isBusinessExceptionSuccess;
  }

  async getOrganizationName(organizationUrlName: string): Promise<{ name: string; label: string; urlName?: string; lowerCaseName?: string }> {
    if (!organizationUrlName) {
      return this.selectedOrganization;
    }
    await this.initializeOrganizationsIfNeeded();
    const organizationLowerCaseName = organizationUrlName.replace(/-/g, '').toLowerCase();
    const organization = this.organizations.find(o => o.urlName === organizationUrlName || o.lowerCaseName === organizationLowerCaseName);
    return organization;
  }

  // organization changed
  async selectOrganization(organizationName: string): Promise<void> {
    if (!organizationName || organizationName === this.selectedOrganization?.name) {
      return;
    }

    this.storageService.clearStorage();
    this.dataAvailabilityService.clear();
    this.navigationService.clear();

    await this.sidepanelService.close();
    await this.initializeOrganizationsIfNeeded();
    await this.authService.setOrganization(organizationName);
    await this.signalRService.restartConnection();
    await this.initializeOrganizationConfigurationUi();
    await this.tenantsService.initializeTenants();
    this.eventBusService.emit(EventType.OrganizationChanged);
  }
}
